import { fetchApi } from '@inmagik/react-crud'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { saveAs } from 'file-saver'
import { useMemo, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import ReactDatePicker from 'react-datepicker'
import { useAuth } from '../../auth'
import MaterialIcon from '../../components/MaterialIcon'
import Paginator from '../../components/Paginator'
import SelectAutoCompleteFilter from '../../components/SelectAutocompleteFilter'
import { API_URL, STATI_PROPOSTA } from '../../constants'
import { useQsFilters } from '../../hooks/filters'
import { useProposteList } from '../../hooks/proposte'
import { proposteInitFilters } from './ProposteFilters'
import { ProposteTable } from './ProposteTable'

export default function Proposte() {
  const { tokens } = useAuth()
  const { filters, uiFilters, setFilters } = useQsFilters(proposteInitFilters)

  const apiFilters = useMemo(
    () => ({
      page: String(filters.page),
      search: filters.search,
      ordering: filters.ordering,
      data_creazione__lte: filters.data_creazione__lte,
      data_creazione__gte: filters.data_creazione__gte,
      cliente: filters.cliente,
      stato: filters.stato,
    }),
    [filters]
  )

  const proposte = useProposteList(apiFilters)

  const [isDownloadingXlsx, setIsDownloadingXlsx] = useState(false)

  return (
    <div className="page">
      <div className="row mt-4">
        <div className="col-12 d-flex justify-content-between align-items-center">
          <h1 className="title">Proposte</h1>
          <Button
            disabled={isDownloadingXlsx}
            onClick={async () => {
              setIsDownloadingXlsx(true)
              try {
                const data = await fetchApi(`${API_URL}/bilance/proposta/xlsx/?${new URLSearchParams(apiFilters)}`, {
                  headers: {
                    Authorization: `Bearer ${tokens?.access}`,
                  },
                })
                const blob = await data.blob()
                saveAs(blob, 'proposte.xlsx')
              } finally {
                setIsDownloadingXlsx(false)
              }
            }}
            variant="outline-secondary"
            className="d-flex align-items-center bg-white me-2 text-secondary"
            type="button"
          >
            <MaterialIcon icon="download" className="me-2" />
            Scarica Excel
            {isDownloadingXlsx && <Spinner animation="border" variant="secondary" className="ms-4" size="sm" />}
          </Button>
        </div>
        <div className="col-12">
          <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded">
            <div className="d-flex align-items-center w-100">
              <div className="d-flex align-items-center">
                <label className="label-m text-body-secondary me-2">Periodo</label>
                <ReactDatePicker
                  selectsRange={true}
                  isClearable
                  placeholderText="Seleziona periodo"
                  className="form-control me-2 text-secondary"
                  startDate={filters.data_creazione__gte !== '' ? dayjs(filters.data_creazione__gte).toDate() : null}
                  endDate={filters.data_creazione__lte !== '' ? dayjs(filters.data_creazione__lte).toDate() : null}
                  dateFormat={'dd/MM/yyyy'}
                  onChange={(date) => {
                    if (!date[0]) {
                      setFilters({
                        data_creazione__gte: '',
                        data_creazione__lte: '',
                        page: 1,
                      })
                    } else {
                      if (date[0]) {
                        setFilters({
                          data_creazione__gte: date[0] ? dayjs(date[0]).format('YYYY-MM-DD') : '',
                          data_creazione__lte: date[1] ? dayjs(date[1]).format('YYYY-MM-DD') : '',

                          page: 1,
                        })
                      }
                    }
                  }}
                />
              </div>
              <div className="d-flex align-items-center ms-3">
                <label className="label-m text-body-secondary me-2">Cliente</label>
                <div style={{ width: '230px' }}>
                  <SelectAutoCompleteFilter
                    className="interventi-cliente-filter"
                    value={uiFilters.cliente ?? ''}
                    placeholder="Cerca cliente"
                    onChange={(value: any) => {
                      if (!value) {
                        return setFilters({
                          cliente: '',
                          page: 1,
                        })
                      } else {
                        setFilters({
                          cliente: value.value,
                          page: 1,
                        })
                      }
                    }}
                    url={`${API_URL}/bilance/cliente/options`}
                    isMulti={false}
                  />
                </div>
              </div>
              <div className="d-flex align-items-center ms-3">
                <label className="label-m text-body-secondary me-2">Stato</label>
                <select
                  className={classNames('form-select me-2', {
                    'text-secondary': uiFilters.stato !== '',
                  })}
                  style={{ width: '170px' }}
                  value={uiFilters.stato ?? ''}
                  onChange={(e) => setFilters({ stato: e.target.value, page: 1 })}
                >
                  <option value="">Tutti gli stati</option>
                  {STATI_PROPOSTA.map((esito) => (
                    <option key={esito.value} value={esito.value}>
                      {esito.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="me-2 text-nowrap">
              Stai visualizzando {proposte.data?.count ?? 0} di {proposte.data?.full_count ?? 0} proposte
            </div>
          </div>
          <ProposteTable proposte={proposte} />
          <Paginator
            count={proposte.data?.count ?? 0}
            currentPage={uiFilters.page}
            goToPage={(page) => setFilters({ page })}
          />
          <div className="position-relative">
            <div
              className="position-absolute d-flex align-items-center"
              style={{
                top: '50%',
                left: 20,
                transform: 'translateY(-50%)',
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  )
}
